import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import confetti from 'canvas-confetti';
import { getApiUrl, getAuthHeaders, apiRequest } from '../config/apiConfig';
import './RSVP.css';

const RSVP = ({ config }) => {
  const rsvpConfig = config.content.rsvp.search;
  const [searchName, setSearchName] = useState('');
  const [guest, setGuest] = useState(null);
  const [multipleMatches, setMultipleMatches] = useState(null);
  const [error, setError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [updatingDay, setUpdatingDay] = useState(null);
  const [savedDays, setSavedDays] = useState({ day1: false, day2: false });
  const [day1FadingOut, setDay1FadingOut] = useState(false);
  const [day2Immediate, setDay2Immediate] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const day1SubmitRef = useRef(null);
  const day2SubmitRef = useRef(null);
  const [dayForms, setDayForms] = useState({
    day1: { 
      attending: null, 
      guests: 1, 
      rsvpFor: null, 
      flyingInDate: '',
      person1Attending: null,
      person2Attending: null
    },
    day2: { 
      attending: null, 
      guests: 1, 
      rsvpFor: null, 
      flyingInDate: '',
      person1Attending: null,
      person2Attending: null
    }
  });

  const parseDateFromString = (ddmmyyyy) => {
    if (!ddmmyyyy) return null;
    const parts = ddmmyyyy.split('-');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return null;
  };

  const formatDateToString = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setGuest(null);
    setMultipleMatches(null);
    setSavedDays({ day1: false, day2: false });
    setDay1FadingOut(false);
    setDay2Immediate(false);
    
    if (!searchName.trim()) {
      setError(rsvpConfig.errorMessages.empty);
      return;
    }
    
    if (searchName.trim().length < rsvpConfig.minNameLength) {
      setError(rsvpConfig.errorMessages.tooShort);
      return;
    }

    setSearchLoading(true);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await apiRequest({
        method: 'get',
        url: getApiUrl('api/rsvp/search'),
        params: { name: searchName },
        headers: authHeaders
      });
      
      console.log('Search response:', response.data);
      
      if (response.data && response.data.multipleMatches && response.data.matches) {
        setMultipleMatches(response.data.matches);
        setError('');
        return;
      }
      
      if (response.data && response.data.success && response.data.guest) {
        const guestData = response.data.guest;
        setGuest(guestData);
        setError('');
        
        // Initialize form with no selections - always start fresh
        setDayForms({
          day1: {
            attending: null,
            guests: 1,
            rsvpFor: null,
            flyingInDate: '',
            person1Attending: null,
            person2Attending: null
          },
          day2: {
            attending: null,
            guests: 1,
            rsvpFor: null,
            flyingInDate: '',
            person1Attending: null,
            person2Attending: null
          }
        });
        setSavedDays({ day1: false, day2: false });
        setDay1FadingOut(false);
        setDay2Immediate(false);
      } else {
        setError(rsvpConfig.errorMessages.unexpected);
      }
    } catch (err) {
      console.error('Search error:', err);
      console.error('Error response:', err.response?.data);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message === 'Network Error' || err.code === 'ECONNREFUSED') {
        setError(rsvpConfig.errorMessages.networkError);
      } else {
        setError(rsvpConfig.errorMessages.generic);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectGuest = async (guestId) => {
    setSearchLoading(true);
    setError('');
    
    try {
      // Fetch full guest details with RSVP data by ID
      const selectedMatch = multipleMatches.find(m => m.id === guestId);
      const authHeaders = await getAuthHeaders();
      const response = await apiRequest({
        method: 'get',
        url: getApiUrl(`api/rsvp/guest/${guestId}`),
        params: { searchName: selectedMatch?.name || searchName },
        headers: authHeaders
      });
      
      if (response.data && response.data.success && response.data.guest) {
        const guestData = response.data.guest;
        setGuest(guestData);
        setMultipleMatches(null);
        
        // Initialize form with no selections - always start fresh
        setDayForms({
          day1: {
            attending: null,
            guests: 1,
            rsvpFor: null,
            flyingInDate: '',
            person1Attending: null,
            person2Attending: null
          },
          day2: {
            attending: null,
            guests: 1,
            rsvpFor: null,
            flyingInDate: '',
            person1Attending: null,
            person2Attending: null
          }
        });
        setSavedDays({ day1: false, day2: false });
        setDay1FadingOut(false);
        setDay2Immediate(false);
        setEditingDay(null);
      }
    } catch (err) {
      console.error('Error selecting guest:', err);
      setError(err.response?.data?.error || rsvpConfig.errorMessages.loadError);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleDayFormChange = (day, field, value) => {
    setDayForms(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  // Convert backend format to person1/person2 statuses
  const convertBackendToCoupleRSVP = (rsvpFor, attending) => {
    if (attending === null || attending === undefined) {
      return { person1Attending: null, person2Attending: null };
    }
    if (attending === false) {
      return { person1Attending: false, person2Attending: false };
    }
    if (attending === true) {
      if (rsvpFor === 'both') return { person1Attending: true, person2Attending: true };
      if (rsvpFor === 'person1_only') return { person1Attending: true, person2Attending: false };
      if (rsvpFor === 'person2_only') return { person1Attending: false, person2Attending: true };
      if (rsvpFor === 'person1') return { person1Attending: true, person2Attending: null };
      if (rsvpFor === 'person2') return { person1Attending: null, person2Attending: true };
    }
    return { person1Attending: null, person2Attending: null };
  };

  // Helper function to get detailed status text for summary cards
  const getDetailedStatusText = (guest, day) => {
    const rsvp = guest.rsvp?.[day];
    if (!guest.isCouple) {
      // Single guest
      if (!rsvp || (rsvp.attending !== true && rsvp.attending !== false)) {
        return 'Not RSVP\'d';
      }
      return rsvp.attending ? rsvpConfig.labels.attending : rsvpConfig.labels.cantMakeIt;
    }
    
    // Couple - need to determine individual statuses
    if (!rsvp || (rsvp.attending !== true && rsvp.attending !== false)) {
      return 'Not RSVP\'d';
    }
    
    const converted = convertBackendToCoupleRSVP(rsvp.rsvpFor, rsvp.attending);
    const p1Status = converted.person1Attending;
    const p2Status = converted.person2Attending;
    
    // Build status text based on each person's status
    const parts = [];
    const p1Name = guest.displayPerson1 || guest.person1;
    const p2Name = guest.displayPerson2 || guest.person2;
    
    if (p1Status === true) {
      parts.push(`${p1Name}: ${rsvpConfig.labels.attending}`);
    } else if (p1Status === false) {
      parts.push(`${p1Name}: ${rsvpConfig.labels.cantMakeIt}`);
    } else {
      parts.push(`${p1Name}: Not RSVP'd`);
    }
    
    if (p2Status === true) {
      parts.push(`${p2Name}: ${rsvpConfig.labels.attending}`);
    } else if (p2Status === false) {
      parts.push(`${p2Name}: ${rsvpConfig.labels.cantMakeIt}`);
    } else {
      parts.push(`${p2Name}: Not RSVP'd`);
    }
    
    return parts.join(', ');
  };

  // Convert person1/person2 statuses to backend format
  const convertCoupleRSVPToBackend = (person1Attending, person2Attending) => {
    const p1 = person1Attending;
    const p2 = person2Attending;
    
    if (p1 === true && p2 === true) return { attending: true, rsvpFor: 'both', guests: 2 };
    if (p1 === true && p2 === false) return { attending: true, rsvpFor: 'person1_only', guests: 1 };
    if (p1 === true && p2 === null) return { attending: true, rsvpFor: 'person1', guests: 1 };
    if (p1 === false && p2 === true) return { attending: true, rsvpFor: 'person2_only', guests: 1 };
    if (p1 === false && p2 === false) return { attending: false, rsvpFor: null, guests: 0 };
    if (p1 === false && p2 === null) return { attending: false, rsvpFor: null, guests: 0 };
    if (p1 === null && p2 === true) return { attending: true, rsvpFor: 'person2', guests: 1 };
    if (p1 === null && p2 === false) return { attending: false, rsvpFor: null, guests: 0 };
    if (p1 === null && p2 === null) return { attending: null, rsvpFor: null, guests: 0 };
    
    return { attending: null, rsvpFor: null, guests: 0 };
  };

  const handleDaySubmit = async (day, e) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    
    const formData = dayForms[day];
    
    // For couples, check if at least one person has made a selection
    if (guest.isCouple) {
      if (formData.person1Attending === null && formData.person2Attending === null) {
        setError(rsvpConfig.errorMessages.noSelection);
        return;
      }
    } else {
      // For single guests, use the old logic
      if (formData.attending === null || formData.attending === undefined) {
        setError(rsvpConfig.errorMessages.noSelection);
        return;
      }
    }
    
    setUpdatingDay(day);
    
    const rsvpSection = document.getElementById('rsvp');
    
    let submissionData;
    if (guest.isCouple) {
      // Convert person1Attending/person2Attending to backend format
      // Use the current formData values directly (not from sessionStorage)
      const converted = convertCoupleRSVPToBackend(formData.person1Attending, formData.person2Attending);
      submissionData = {
        attending: converted.attending,
        rsvpFor: converted.rsvpFor,
        guests: converted.guests
      };
    } else {
      // Single guest logic
      submissionData = {
        attending: formData.attending,
        rsvpFor: 'person1',
        guests: formData.attending ? 1 : 0
      };
    }
    
    try {
      const authHeaders = await getAuthHeaders();
      const response = await apiRequest({
        method: 'post',
        url: getApiUrl('api/rsvp/update'),
        data: {
          guestId: guest.id,
          day: day,
          attending: submissionData.attending,
          guests: submissionData.guests,
          rsvpFor: submissionData.rsvpFor,
          rsvpBy: guest.searchingAs || 'person1',
          flyingInDate: formData.flyingInDate || ''
        },
        headers: authHeaders
      });

      if (response.data.success) {
        setGuest(prev => ({
          ...prev,
          rsvp: response.data.rsvp
        }));
        // Update dayForms to reflect the submitted data for couples
        if (guest.isCouple) {
          setDayForms(prev => ({
            ...prev,
            [day]: {
              ...prev[day],
              person1Attending: formData.person1Attending,
              person2Attending: formData.person2Attending,
              attending: submissionData.attending,
              rsvpFor: submissionData.rsvpFor,
              guests: submissionData.guests
            }
          }));
        }
        setError('');
        
        if (submissionData.attending) {
          triggerConfetti(guest.isCouple, submissionData.rsvpFor);
        }
        
        if (day === 'day1') {
          setDay1FadingOut(true);
          setTimeout(() => {
            setSavedDays(prev => ({ ...prev, day1: true }));
            if (rsvpSection) {
              rsvpSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 400);
        } else {
          setSavedDays(prev => ({ ...prev, [day]: true }));
          if (day === 'day2') {
            setTimeout(() => {
              const summary = document.getElementById('rsvp-summary');
              if (summary) {
                const summaryRect = summary.getBoundingClientRect();
                const sectionRect = rsvpSection ? rsvpSection.getBoundingClientRect() : null;
                if (sectionRect && (summaryRect.top < sectionRect.top || summaryRect.bottom > sectionRect.bottom)) {
                  rsvpSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  setTimeout(() => {
                    summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 200);
                } else {
                  summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              } else if (rsvpSection) {
                rsvpSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          } else {
            setTimeout(() => {
              if (rsvpSection) {
                rsvpSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }
        }
        setEditingDay(null);
      }
    } catch (err) {
      console.error('RSVP update error:', err);
      console.error('Error response:', err.response?.data);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message === 'Network Error' || err.code === 'ECONNREFUSED') {
        setError(rsvpConfig.errorMessages.networkError);
      } else {
        setError(rsvpConfig.errorMessages.saveError);
      }
    } finally {
      setUpdatingDay(null);
    }
  };

  const getDayLabel = (day) => {
    if (day === 'day1') {
      return config.content.schedule.days?.[0]?.date || 'Day 1 - July 8, 2026';
    }
    return config.content.schedule.days?.[1]?.date || 'Day 2 - July 9, 2026';
  };

  const getDayEvents = (day) => {
    if (day === 'day1') {
      return config.content.schedule.days?.[0]?.events || [];
    }
    return config.content.schedule.days?.[1]?.events || [];
  };

  useEffect(() => {
    if (updatingDay) return;
    
    if (editingDay === 'day1' || (!savedDays.day1 && editingDay !== 'day2')) {
      setTimeout(() => {
        if (day1SubmitRef.current) {
          const rsvpSection = document.getElementById('rsvp');
          if (rsvpSection) {
            const buttonRect = day1SubmitRef.current.getBoundingClientRect();
            const sectionRect = rsvpSection.getBoundingClientRect();
            if (buttonRect.bottom > window.innerHeight || buttonRect.top < sectionRect.top) {
              day1SubmitRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
            }
          }
        }
      }, 300);
    }
  }, [editingDay, savedDays.day1, updatingDay]);

  useEffect(() => {
    if (updatingDay) return; // Don't scroll while saving
    
    if ((savedDays.day1 && !savedDays.day2 && editingDay !== 'day1') || editingDay === 'day2') {
      setTimeout(() => {
        if (day2SubmitRef.current) {
          const rsvpSection = document.getElementById('rsvp');
          if (rsvpSection) {
            const buttonRect = day2SubmitRef.current.getBoundingClientRect();
            const sectionRect = rsvpSection.getBoundingClientRect();
            if (buttonRect.bottom > window.innerHeight || buttonRect.top < sectionRect.top) {
              day2SubmitRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
            }
          }
        }
      }, 300);
    }
  }, [editingDay, savedDays.day1, savedDays.day2, updatingDay]);

  const triggerConfetti = (isCouple, rsvpFor) => {
    const isBothAttending = isCouple && rsvpFor === 'both';
    const confettiConfig = config.design.confetti;
    confetti({
      particleCount: isBothAttending ? confettiConfig.particleCount.couple : confettiConfig.particleCount.single,
      spread: isBothAttending ? confettiConfig.spread.couple : confettiConfig.spread.single,
      origin: { y: confettiConfig.originY },
      colors: confettiConfig.colors
    });
  };

  return (
    <section id="rsvp" className="rsvp section">
      <div className="container">
        <h2 className="section-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
          RSVP
        </h2>
        <p className="rsvp-message" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary, fontWeight: 500 }}>
          {config.content.rsvp.message}
        </p>

        {!guest && !multipleMatches ? (
          <div className="rsvp-search">
            <form onSubmit={handleSearch} className="search-form" style={{ fontFamily: config.design.fonts.body }}>
              {error && <div className="error-message">{error}</div>}
              
              <div className="form-group">
                <label htmlFor="search-name">Enter your name to RSVP *</label>
                <input
                  type="text"
                  id="search-name"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder={rsvpConfig.labels.searchPlaceholder}
                  minLength={rsvpConfig.minNameLength}
                  required
                  style={{ fontFamily: config.design.fonts.body }}
                />
                {searchName.length > 0 && searchName.length < rsvpConfig.minNameLength && (
                  <p style={{ fontSize: '0.85rem', color: '#dc3545', marginTop: '5px' }}>
                    {rsvpConfig.errorMessages.tooShort}
                  </p>
                )}
              </div>

              <button 
                type="submit" 
                className={`submit-button ${searchLoading ? 'search-loading' : ''}`}
                disabled={searchLoading || searchName.trim().length < rsvpConfig.minNameLength}
                style={{ 
                  fontFamily: config.design.fonts.body,
                  backgroundColor: config.design.colors.primary,
                  color: config.design.colors.white
                }}
              >
                {searchLoading ? (
                  <span className="bear-loading">
                    <span className="bear-emoji">🐻</span> with me
                  </span>
                ) : 'Search'}
              </button>
            </form>
          </div>
        ) : multipleMatches ? (
          <div className="rsvp-multiple-matches">
            <div className="matches-message" style={{ fontFamily: config.design.fonts.body }}>
              <h3 style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary, fontWeight: 500 }}>
                {rsvpConfig.labels.multipleMatchesTitle}
              </h3>
              <p style={{ color: config.design.colors.secondary }}>
                {rsvpConfig.labels.multipleMatchesSubtitle}
              </p>
            </div>
            <div className="matches-list">
              {multipleMatches.map((match) => (
                <button
                  key={match.id}
                  onClick={() => handleSelectGuest(match.id)}
                  className="match-button"
                  disabled={searchLoading}
                  style={{
                    fontFamily: config.design.fonts.body,
                    borderColor: config.design.colors.primary,
                    color: config.design.colors.primary
                  }}
                >
                  {match.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setMultipleMatches(null);
                setSearchName('');
                setError('');
              }}
              className="back-button"
              style={{ 
                fontFamily: config.design.fonts.body,
                color: config.design.colors.primary
              }}
            >
              {rsvpConfig.labels.searchAgain}
            </button>
          </div>
        ) : (
          <div className="rsvp-guest-info">
            <div className="guest-found" style={{ fontFamily: config.design.fonts.body }}>
              <h3 style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary, fontWeight: 500 }}>
                {guest.welcomeMessage || guest.welcomeText || rsvpConfig.labels.welcome}{guest.isCouple ? `, ${guest.displayPerson1 || guest.person1} & ${guest.displayPerson2 || guest.person2}` : `, ${guest.displayPerson1 || guest.person1}`}!
              </h3>
              <button
              onClick={() => {
                setGuest(null);
                setSearchName('');
                setError('');
                setSavedDays({ day1: false, day2: false });
                setDay1FadingOut(false);
                setDay2Immediate(false);
                setEditingDay(null);
              }}
                className="back-button"
                style={{ 
                  fontFamily: config.design.fonts.body,
                  borderColor: config.design.colors.primary,
                  color: config.design.colors.primary
                }}
              >
                {rsvpConfig.labels.notYou}
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {(editingDay === 'day1' || (!savedDays.day1 && editingDay !== 'day2')) && (
              <div className={`day-rsvp day-rsvp-day1 ${day1FadingOut ? 'fade-out' : ''}`}>
              <h4 className="day-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary, fontWeight: 500 }}>
                {getDayLabel('day1')}
              </h4>
              <div className="day-events" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary, fontWeight: 500 }}>
                {getDayEvents('day1').map((event, idx) => (
                  <span key={idx}>{event.title}{idx < getDayEvents('day1').length - 1 ? ', ' : ''}</span>
                ))}
              </div>
              <form onSubmit={(e) => handleDaySubmit('day1', e)} className="day-form">
                {guest.isCouple ? (
                  <div className="form-group">
                    <div className="couple-rsvp-boxes">
                      {/* Person 1 Box */}
                      <div className="person-rsvp-box" style={{ 
                        border: `2px solid ${config.design.colors.primary}`, 
                        borderRadius: '8px', 
                        padding: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)'
                      }}>
                        <div style={{ 
                          fontFamily: config.design.fonts.heading, 
                          color: config.design.colors.primary, 
                          fontWeight: 600,
                          marginBottom: '15px',
                          fontSize: '1.1rem'
                        }}>
                          {guest.displayPerson1 || guest.person1}
                        </div>
                        <div className="radio-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <label 
                            className="radio-label" 
                            style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                            onClick={(e) => {
                              // Ensure input is clicked when label is clicked (mobile fallback)
                              if (e.target.tagName !== 'INPUT') {
                                const input = e.currentTarget.querySelector('input[type="radio"]');
                                if (input) {
                                  input.click();
                                }
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="day1-person1"
                              value="attending"
                              checked={dayForms.day1.person1Attending === true}
                              onChange={() => {
                                handleDayFormChange('day1', 'person1Attending', dayForms.day1.person1Attending === true ? null : true);
                              }}
                              style={{ marginRight: '10px' }}
                            />
                            <span>{rsvpConfig.labels.attending}</span>
                          </label>
                          <label 
                            className="radio-label" 
                            style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                            onClick={(e) => {
                              // Ensure input is clicked when label is clicked (mobile fallback)
                              if (e.target.tagName !== 'INPUT') {
                                const input = e.currentTarget.querySelector('input[type="radio"]');
                                if (input) {
                                  input.click();
                                }
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="day1-person1"
                              value="not-attending"
                              checked={dayForms.day1.person1Attending === false}
                              onChange={() => {
                                handleDayFormChange('day1', 'person1Attending', dayForms.day1.person1Attending === false ? null : false);
                              }}
                              style={{ marginRight: '10px' }}
                            />
                            <span>{rsvpConfig.labels.cantMakeIt}</span>
                          </label>
                        </div>
                      </div>

                      {/* Person 2 Box */}
                      <div className="person-rsvp-box" style={{ 
                        border: `2px solid ${config.design.colors.primary}`, 
                        borderRadius: '8px', 
                        padding: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)'
                      }}>
                        <div style={{ 
                          fontFamily: config.design.fonts.heading, 
                          color: config.design.colors.primary, 
                          fontWeight: 600,
                          marginBottom: '15px',
                          fontSize: '1.1rem'
                        }}>
                          {guest.displayPerson2 || guest.person2}
                        </div>
                        <div className="radio-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <label 
                            className="radio-label" 
                            style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                            onClick={(e) => {
                              // Ensure input is clicked when label is clicked (mobile fallback)
                              if (e.target.tagName !== 'INPUT') {
                                const input = e.currentTarget.querySelector('input[type="radio"]');
                                if (input) {
                                  input.click();
                                }
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="day1-person2"
                              value="attending"
                              checked={dayForms.day1.person2Attending === true}
                              onChange={() => {
                                handleDayFormChange('day1', 'person2Attending', dayForms.day1.person2Attending === true ? null : true);
                              }}
                              style={{ marginRight: '10px' }}
                            />
                            <span>{rsvpConfig.labels.attending}</span>
                          </label>
                          <label 
                            className="radio-label" 
                            style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                            onClick={(e) => {
                              // Ensure input is clicked when label is clicked (mobile fallback)
                              if (e.target.tagName !== 'INPUT') {
                                const input = e.currentTarget.querySelector('input[type="radio"]');
                                if (input) {
                                  input.click();
                                }
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="day1-person2"
                              value="not-attending"
                              checked={dayForms.day1.person2Attending === false}
                              onChange={() => {
                                handleDayFormChange('day1', 'person2Attending', dayForms.day1.person2Attending === false ? null : false);
                              }}
                              style={{ marginRight: '10px' }}
                            />
                            <span>{rsvpConfig.labels.cantMakeIt}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <div className="radio-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <label 
                        className="radio-label" 
                        style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                        onClick={(e) => {
                          // Ensure input is clicked when label is clicked (mobile fallback)
                          if (e.target.tagName !== 'INPUT') {
                            const input = e.currentTarget.querySelector('input[type="radio"]');
                            if (input) {
                              input.click();
                            }
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="day1-attending"
                          value="attending"
                          checked={dayForms.day1.attending === true}
                          onChange={() => {
                            handleDayFormChange('day1', 'attending', dayForms.day1.attending === true ? null : true);
                          }}
                          style={{ marginRight: '10px' }}
                        />
                        <span>{rsvpConfig.labels.attending}</span>
                      </label>
                      <label 
                        className="radio-label" 
                        style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                        onClick={(e) => {
                          // Ensure input is clicked when label is clicked (mobile fallback)
                          if (e.target.tagName !== 'INPUT') {
                            const input = e.currentTarget.querySelector('input[type="radio"]');
                            if (input) {
                              input.click();
                            }
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="day1-attending"
                          value="not-attending"
                          checked={dayForms.day1.attending === false}
                          onChange={() => {
                            handleDayFormChange('day1', 'attending', dayForms.day1.attending === false ? null : false);
                          }}
                          style={{ marginRight: '10px' }}
                        />
                        <span>{rsvpConfig.labels.cantMakeIt}</span>
                      </label>
                    </div>
                  </div>
                )}

                {((guest.isCouple && (dayForms.day1.person1Attending === true || dayForms.day1.person2Attending === true)) || 
                  (!guest.isCouple && dayForms.day1.attending)) && (
                  <div className="form-group">
                    <label htmlFor="day1-flyingInDate" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.primary, fontWeight: 500 }}>
                      {rsvpConfig.labels.dateFlyingIn} <span style={{ color: config.design.colors.secondary, fontSize: '0.9rem', fontWeight: 'normal' }}>{rsvpConfig.labels.dateFlyingInOptional}</span>
                    </label>
                    <DatePicker
                      id="day1-flyingInDate"
                      selected={parseDateFromString(dayForms.day1.flyingInDate)}
                      onChange={(date) => {
                        const formattedDate = formatDateToString(date);
                        handleDayFormChange('day1', 'flyingInDate', formattedDate);
                      }}
                      dateFormat="dd-MM-yyyy"
                      placeholderText="Select date"
                      isClearable
                      minDate={new Date(config.date.rsvpDatePicker.minDate.year, config.date.rsvpDatePicker.minDate.month, config.date.rsvpDatePicker.minDate.day)}
                      maxDate={new Date(config.date.rsvpDatePicker.maxDate.year, config.date.rsvpDatePicker.maxDate.month, config.date.rsvpDatePicker.maxDate.day)}
                      className="date-picker-input"
                      wrapperClassName="date-picker-wrapper"
                      calendarClassName="date-picker-calendar"
                    />
                  </div>
                )}

                {savedDays.day1 && (
                  <div className="success-message" style={{
                    fontFamily: config.design.fonts.body,
                    color: config.design.colors.success,
                    backgroundColor: config.design.colors.successBackground,
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    border: `1px solid ${config.design.colors.successBorder}`
                  }}>
                    {rsvpConfig.successMessages.day1}
                  </div>
                )}

                <button
                  ref={day1SubmitRef}
                  type="submit"
                  className={`submit-button day-submit ${updatingDay === 'day1' ? 'search-loading' : ''}`}
                  disabled={updatingDay === 'day1' || (guest.isCouple ? (dayForms.day1.person1Attending === null && dayForms.day1.person2Attending === null) : dayForms.day1.attending === null)}
                  style={{ 
                    fontFamily: config.design.fonts.body,
                    backgroundColor: config.design.colors.primary,
                    color: '#ffffff',
                    opacity: (guest.isCouple ? (dayForms.day1.person1Attending === null && dayForms.day1.person2Attending === null) : dayForms.day1.attending === null) ? 0.6 : 1
                  }}
                >
                  {updatingDay === 'day1' ? (
                    <span className="bear-loading">
                      <span className="bear-emoji">🐻</span> with me
                    </span>
                  ) : rsvpConfig.labels.saveDay1}
                </button>
              </form>
            </div>
            )}

            {((savedDays.day1 && !savedDays.day2) || editingDay === 'day2') && editingDay !== 'day1' && (
              <div 
                id="day2-rsvp"
                className={`day-rsvp day-rsvp-day2 ${day2Immediate ? 'immediate' : ''}`}
              >
              <h4 className="day-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary, fontWeight: 500 }}>
                {getDayLabel('day2')}
              </h4>
              <div className="day-events" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary, fontWeight: 500 }}>
                {getDayEvents('day2').map((event, idx) => (
                  <span key={idx}>{event.title}{idx < getDayEvents('day2').length - 1 ? ', ' : ''}</span>
                ))}
              </div>
              <form onSubmit={(e) => handleDaySubmit('day2', e)} className="day-form">
                {guest.isCouple ? (
                  <div className="form-group">
                    <div className="couple-rsvp-boxes">
                      {/* Person 1 Box */}
                      <div className="person-rsvp-box" style={{ 
                        border: `2px solid ${config.design.colors.primary}`, 
                        borderRadius: '8px', 
                        padding: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)'
                      }}>
                        <div style={{ 
                          fontFamily: config.design.fonts.heading, 
                          color: config.design.colors.primary, 
                          fontWeight: 600,
                          marginBottom: '15px',
                          fontSize: '1.1rem'
                        }}>
                          {guest.displayPerson1 || guest.person1}
                        </div>
                        <div className="radio-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <label 
                            className="radio-label" 
                            style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                            onClick={(e) => {
                              // Ensure input is clicked when label is clicked (mobile fallback)
                              if (e.target.tagName !== 'INPUT') {
                                const input = e.currentTarget.querySelector('input[type="radio"]');
                                if (input) {
                                  input.click();
                                }
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="day2-person1"
                              value="attending"
                              checked={dayForms.day2.person1Attending === true}
                              onChange={() => {
                                handleDayFormChange('day2', 'person1Attending', dayForms.day2.person1Attending === true ? null : true);
                              }}
                              style={{ marginRight: '10px' }}
                            />
                            <span>{rsvpConfig.labels.attending}</span>
                          </label>
                          <label 
                            className="radio-label" 
                            style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                            onClick={(e) => {
                              // Ensure input is clicked when label is clicked (mobile fallback)
                              if (e.target.tagName !== 'INPUT') {
                                const input = e.currentTarget.querySelector('input[type="radio"]');
                                if (input) {
                                  input.click();
                                }
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="day2-person1"
                              value="not-attending"
                              checked={dayForms.day2.person1Attending === false}
                              onChange={() => {
                                handleDayFormChange('day2', 'person1Attending', dayForms.day2.person1Attending === false ? null : false);
                              }}
                              style={{ marginRight: '10px' }}
                            />
                            <span>{rsvpConfig.labels.cantMakeIt}</span>
                          </label>
                        </div>
                      </div>

                      {/* Person 2 Box */}
                      <div className="person-rsvp-box" style={{ 
                        border: `2px solid ${config.design.colors.primary}`, 
                        borderRadius: '8px', 
                        padding: '20px',
                        backgroundColor: 'rgba(255, 255, 255, 0.5)'
                      }}>
                        <div style={{ 
                          fontFamily: config.design.fonts.heading, 
                          color: config.design.colors.primary, 
                          fontWeight: 600,
                          marginBottom: '15px',
                          fontSize: '1.1rem'
                        }}>
                          {guest.displayPerson2 || guest.person2}
                        </div>
                        <div className="radio-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <label 
                            className="radio-label" 
                            style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                            onClick={(e) => {
                              // Ensure input is clicked when label is clicked (mobile fallback)
                              if (e.target.tagName !== 'INPUT') {
                                const input = e.currentTarget.querySelector('input[type="radio"]');
                                if (input) {
                                  input.click();
                                }
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="day2-person2"
                              value="attending"
                              checked={dayForms.day2.person2Attending === true}
                              onChange={() => {
                                handleDayFormChange('day2', 'person2Attending', dayForms.day2.person2Attending === true ? null : true);
                              }}
                              style={{ marginRight: '10px' }}
                            />
                            <span>{rsvpConfig.labels.attending}</span>
                          </label>
                          <label 
                            className="radio-label" 
                            style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                            onClick={(e) => {
                              // Ensure input is clicked when label is clicked (mobile fallback)
                              if (e.target.tagName !== 'INPUT') {
                                const input = e.currentTarget.querySelector('input[type="radio"]');
                                if (input) {
                                  input.click();
                                }
                              }
                            }}
                          >
                            <input
                              type="radio"
                              name="day2-person2"
                              value="not-attending"
                              checked={dayForms.day2.person2Attending === false}
                              onChange={() => {
                                handleDayFormChange('day2', 'person2Attending', dayForms.day2.person2Attending === false ? null : false);
                              }}
                              style={{ marginRight: '10px' }}
                            />
                            <span>{rsvpConfig.labels.cantMakeIt}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="form-group">
                    <div className="radio-group" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <label 
                        className="radio-label" 
                        style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                        onClick={(e) => {
                          // Ensure input is clicked when label is clicked (mobile fallback)
                          if (e.target.tagName !== 'INPUT') {
                            const input = e.currentTarget.querySelector('input[type="radio"]');
                            if (input) {
                              input.click();
                            }
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="day2-attending"
                          value="attending"
                          checked={dayForms.day2.attending === true}
                          onChange={() => {
                            handleDayFormChange('day2', 'attending', dayForms.day2.attending === true ? null : true);
                          }}
                          style={{ marginRight: '10px' }}
                        />
                        <span>{rsvpConfig.labels.attending}</span>
                      </label>
                      <label 
                        className="radio-label" 
                        style={{ fontFamily: config.design.fonts.body, cursor: 'pointer', color: config.design.colors.primary, fontWeight: 500 }}
                        onClick={(e) => {
                          // Ensure input is clicked when label is clicked (mobile fallback)
                          if (e.target.tagName !== 'INPUT') {
                            const input = e.currentTarget.querySelector('input[type="radio"]');
                            if (input) {
                              input.click();
                            }
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="day2-attending"
                          value="not-attending"
                          checked={dayForms.day2.attending === false}
                          onChange={() => {
                            handleDayFormChange('day2', 'attending', dayForms.day2.attending === false ? null : false);
                          }}
                          style={{ marginRight: '10px' }}
                        />
                        <span>{rsvpConfig.labels.cantMakeIt}</span>
                      </label>
                    </div>
                  </div>
                )}

                {!(guest.rsvp?.day1?.attending) && (
                  ((guest.isCouple && (dayForms.day2.person1Attending === true || dayForms.day2.person2Attending === true)) || 
                   (!guest.isCouple && dayForms.day2.attending)) && (
                    <div className="form-group">
                      <label htmlFor="day2-flyingInDate" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.primary, fontWeight: 500 }}>
                        {rsvpConfig.labels.dateFlyingIn} <span style={{ color: config.design.colors.secondary, fontSize: '0.9rem', fontWeight: 'normal' }}>{rsvpConfig.labels.dateFlyingInOptional}</span>
                      </label>
                      <DatePicker
                        id="day2-flyingInDate"
                        selected={parseDateFromString(dayForms.day2.flyingInDate)}
                        onChange={(date) => {
                          const formattedDate = formatDateToString(date);
                          handleDayFormChange('day2', 'flyingInDate', formattedDate);
                        }}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="Select date"
                        isClearable
                        minDate={new Date(2026, 6, 6)}
                        maxDate={new Date(2026, 6, 9)}
                        className="date-picker-input"
                        wrapperClassName="date-picker-wrapper"
                        calendarClassName="date-picker-calendar"
                      />
                    </div>
                  )
                )}

                {savedDays.day2 && (
                  <div className="success-message" style={{
                    fontFamily: config.design.fonts.body,
                    color: config.design.colors.success,
                    backgroundColor: config.design.colors.successBackground,
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '15px',
                    border: `1px solid ${config.design.colors.successBorder}`
                  }}>
                    {rsvpConfig.successMessages.day2}
                  </div>
                )}

                <button
                  ref={day2SubmitRef}
                  type="submit"
                  className={`submit-button day-submit ${updatingDay === 'day2' ? 'search-loading' : ''}`}
                  disabled={updatingDay === 'day2' || (guest.isCouple ? (dayForms.day2.person1Attending === null && dayForms.day2.person2Attending === null) : dayForms.day2.attending === null)}
                  style={{ 
                    fontFamily: config.design.fonts.body,
                    backgroundColor: config.design.colors.primary,
                    color: '#ffffff',
                    opacity: (guest.isCouple ? (dayForms.day2.person1Attending === null && dayForms.day2.person2Attending === null) : dayForms.day2.attending === null) ? 0.6 : 1
                  }}
                >
                  {updatingDay === 'day2' ? (
                    <span className="bear-loading">
                      <span className="bear-emoji">🐻</span> with me
                    </span>
                  ) : rsvpConfig.labels.saveDay2}
                </button>
              </form>
            </div>
            )}

            {/* RSVP Summary - Shows after both days are saved */}
            {savedDays.day1 && savedDays.day2 && !editingDay && (
              <div id="rsvp-summary" className="rsvp-summary fade-in-up">
                <h3 className="summary-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary, fontWeight: 500 }}>
                  {rsvpConfig.labels.summaryTitle}
                </h3>
                <p className="summary-subtitle" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary, fontWeight: 500 }}>
                  {rsvpConfig.labels.summarySubtitle}
                </p>

                <div className="summary-cards">
                  <div className="summary-card">
                    <div className="summary-card-header">
                      <h4 className="summary-card-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
                        {getDayLabel('day1')}
                      </h4>
                      <button
                        onClick={() => {
                          setEditingDay('day1');
                          setSavedDays(prev => ({ ...prev, day1: false }));
                          setDay1FadingOut(false);
                          setDay2Immediate(false);
                          // Load RSVP data from backend and convert to person1/person2 format
                          if (guest.rsvp?.day1 && guest.isCouple) {
                            const converted = convertBackendToCoupleRSVP(guest.rsvp.day1.rsvpFor, guest.rsvp.day1.attending);
                            setDayForms(prev => ({
                              ...prev,
                              day1: {
                                ...prev.day1,
                                person1Attending: converted.person1Attending,
                                person2Attending: converted.person2Attending,
                                attending: guest.rsvp.day1.attending || false,
                                guests: guest.rsvp.day1.guests || (guest.isCouple ? 2 : 1),
                                rsvpFor: guest.rsvp.day1.rsvpFor || (guest.isCouple ? 'both' : 'person1'),
                                flyingInDate: guest.rsvp.day1.flyingInDate || ''
                              }
                            }));
                          } else if (guest.rsvp?.day1) {
                            // Single guest - initialize normally
                            setDayForms(prev => ({
                              ...prev,
                              day1: {
                                attending: guest.rsvp.day1.attending || false,
                                guests: guest.rsvp.day1.guests || 1,
                                rsvpFor: 'person1',
                                flyingInDate: guest.rsvp.day1.flyingInDate || '',
                                person1Attending: null,
                                person2Attending: null
                              }
                            }));
                          }
                          setTimeout(() => {
                            const rsvpSection = document.getElementById('rsvp');
                            if (rsvpSection) {
                              rsvpSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        }}
                        className="edit-button"
                        style={{
                          fontFamily: config.design.fonts.body,
                          backgroundColor: 'transparent',
                          color: config.design.colors.primary,
                          border: `1px solid ${config.design.colors.primary}`
                        }}
                      >
                        {rsvpConfig.labels.edit}
                      </button>
                    </div>
                    <div className="summary-card-events" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary }}>
                      {getDayEvents('day1').map((event, idx) => (
                        <span key={idx}>{event.title}{idx < getDayEvents('day1').length - 1 ? ', ' : ''}</span>
                      ))}
                    </div>
                    <div className="summary-card-content" style={{ fontFamily: config.design.fonts.body }}>
                      <div className="summary-item">
                        <span className="summary-label">{rsvpConfig.labels.status}</span>
                        <span className={`summary-value ${guest.rsvp?.day1?.attending ? 'attending' : 'not-attending'}`} style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                          {getDetailedStatusText(guest, 'day1')}
                        </span>
                      </div>
                      {guest.rsvp?.day1?.attending && (
                        <div className="summary-item">
                          <span className="summary-label">{rsvpConfig.labels.guests}</span>
                          <span className="summary-value">{guest.rsvp.day1.guests}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="summary-card">
                    <div className="summary-card-header">
                      <h4 className="summary-card-title" style={{ fontFamily: config.design.fonts.heading, color: config.design.colors.primary }}>
                        {getDayLabel('day2')}
                      </h4>
                      <button
                        onClick={() => {
                          setEditingDay('day2');
                          setSavedDays(prev => ({ ...prev, day2: false }));
                          setDay1FadingOut(false);
                          setDay2Immediate(false);
                          // Load RSVP data from backend and convert to person1/person2 format
                          if (guest.rsvp?.day2 && guest.isCouple) {
                            const converted = convertBackendToCoupleRSVP(guest.rsvp.day2.rsvpFor, guest.rsvp.day2.attending);
                            setDayForms(prev => ({
                              ...prev,
                              day2: {
                                ...prev.day2,
                                person1Attending: converted.person1Attending,
                                person2Attending: converted.person2Attending,
                                attending: guest.rsvp.day2.attending || false,
                                guests: guest.rsvp.day2.guests || (guest.isCouple ? 2 : 1),
                                rsvpFor: guest.rsvp.day2.rsvpFor || (guest.isCouple ? 'both' : 'person1'),
                                flyingInDate: guest.rsvp.day2.flyingInDate || ''
                              }
                            }));
                          } else if (guest.rsvp?.day2) {
                            // Single guest - initialize normally
                            setDayForms(prev => ({
                              ...prev,
                              day2: {
                                attending: guest.rsvp.day2.attending || false,
                                guests: guest.rsvp.day2.guests || 1,
                                rsvpFor: 'person1',
                                flyingInDate: guest.rsvp.day2.flyingInDate || '',
                                person1Attending: null,
                                person2Attending: null
                              }
                            }));
                          }
                          setTimeout(() => {
                            const rsvpSection = document.getElementById('rsvp');
                            if (rsvpSection) {
                              rsvpSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 100);
                        }}
                        className="edit-button"
                        style={{
                          fontFamily: config.design.fonts.body,
                          backgroundColor: 'transparent',
                          color: config.design.colors.primary,
                          border: `1px solid ${config.design.colors.primary}`
                        }}
                      >
                        {rsvpConfig.labels.edit}
                      </button>
                    </div>
                    <div className="summary-card-events" style={{ fontFamily: config.design.fonts.body, color: config.design.colors.secondary }}>
                      {getDayEvents('day2').map((event, idx) => (
                        <span key={idx}>{event.title}{idx < getDayEvents('day2').length - 1 ? ', ' : ''}</span>
                      ))}
                    </div>
                    <div className="summary-card-content" style={{ fontFamily: config.design.fonts.body }}>
                      <div className="summary-item">
                        <span className="summary-label">{rsvpConfig.labels.status}</span>
                        <span className={`summary-value ${guest.rsvp?.day2?.attending ? 'attending' : 'not-attending'}`} style={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>
                          {getDetailedStatusText(guest, 'day2')}
                        </span>
                      </div>
                      {guest.rsvp?.day2?.attending && (
                        <div className="summary-item">
                          <span className="summary-label">{rsvpConfig.labels.guests}</span>
                          <span className="summary-value">{guest.rsvp.day2.guests}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default RSVP;